

import React, { useState, useEffect } from "react";
import { Box, Checkbox, Dialog, Divider, FormControlLabel, Typography } from "@mui/material";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../helpers/methods";
import CustomLoader from "../../../../../global/components/CustomLoader/CustomLoader";
import {
  getAllTeamDataProject,
  getAllTeamMembersDataProject,
} from "./PersonalDetailsService";
import {
  addProjectDetail,
  getDeveloper,
  getProjectByDeveloper,
  getProjectDetails,
  updateProjectDetail,
} from "../StepperService";
import MetaListStyles from "../../../../MetaData/components/MetaList/MetaList.styles";
import CustomPageHeaderStyles from "../../../../../global/components/CustomPageHeader/CustomPageHeader.styles";
import { getLocationMeta } from "../../../../MetaData/components/MetaList/components/ProjectLocationList/projectLocationService";
import { getZone } from "../../../../MetaData/components/MetaList/components/ZoneList/ZoneService";
import { validatePersonalDetail } from "./Validation";
import { YhButton, YhInput, YhMultiSelect, YhSelect } from "../../../../../ui";
import notifiers from "../../../../../global/constants/NotificationConstants";
import MapPicker from "./MapPicker";
import OrganizationSelect from "../../../../../Shared/Organization/OrganizationSelect";

interface CustomProps {
  nextStep?: () => void | undefined;
  userId: String;
  idd: string;
  setIdd: Function;
  fromSettingOrLogout?: any;
  data: any;
}

const toId = (v: any) => {
  if (v == null || v === "") return "";
  if (typeof v === "object") {
    return String(v._id || v.id || v.$oid || "");
  }
  return String(v);
};

const isObjectId = (value: any) =>
  /^[a-f0-9]{24}$/i.test(String(value || "").trim());

/** Keep a team only when it still exists and has a name. A deleted team must not show as a raw id. */
const namedTeamRole = (id: any, matched: any) => {
  const name = String(matched?.team_name || matched?.name || "").trim();
  if (!matched || !name || isObjectId(name)) return null;
  return { id: String(matched._id || id), value: name };
};

/** AssignTo may be string ("'id','id'"), JSON string, or array of ids/objects */
const parseAssignTeamIds = (raw: any): string[] => {
  if (raw == null || raw === "" || raw === "NA") return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => toId(item))
      .filter((id) => id && id !== "NA");
  }
  if (typeof raw === "string") {
    let trimmed = raw.trim();
    // Handle double-encoded values like: "\"'686cd899...'\""
    try {
      let parsed: any = trimmed;
      for (let i = 0; i < 3; i++) {
        if (typeof parsed !== "string") break;
        const next = JSON.parse(parsed);
        parsed = next;
      }
      if (Array.isArray(parsed)) return parseAssignTeamIds(parsed);
      if (typeof parsed === "string") trimmed = parsed;
    } catch {
      // not JSON
    }
    return trimmed
      .replace(/^['"]+|['"]+$/g, "")
      .replace(/'/g, "")
      .split(",")
      .map((s) => s.trim().replace(/^["']+|["']+$/g, ""))
      .filter((id) => id && id !== "NA");
  }
  return [];
};

const parseAssignUserIds = (raw: any): string[] => {
  if (raw == null || raw === "" || raw === "NA") return [];
  if (Array.isArray(raw)) {
    return raw.map((item) => toId(item)).filter(Boolean);
  }
  if (typeof raw === "string") {
    let trimmed = raw.trim();
    try {
      let parsed: any = trimmed;
      for (let i = 0; i < 3; i++) {
        if (typeof parsed !== "string") break;
        parsed = JSON.parse(parsed);
      }
      if (Array.isArray(parsed)) return parseAssignUserIds(parsed);
      if (typeof parsed === "string") trimmed = parsed;
    } catch {
      // not JSON
    }
    return trimmed
      .replace(/^['"]+|['"]+$/g, "")
      .replace(/'/g, "")
      .split(",")
      .map((s) => s.trim().replace(/^["']+|["']+$/g, ""))
      .filter(Boolean);
  }
  return [];
};

const PersonalDetails = (props: CustomProps) => {
  const [state, setState] = useState<any>({ leadOptionMembers: [] });
  const [personalState, setPersonalState] = useState<any>({ loading: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const uniqueLeadOptionMembers = Array.from(
    new Map(
      (state?.leadOptionMembers || []).map((member: any) => [member.id, member])
    ).values()
  );
  const uniqueSelectedOptionMembers = Array.from(
    new Map(
      (personalState?.AssignToUser || []).map((member: any) => [
        member.id,
        member,
      ])
    ).values()
  );
  const setPersonalDetailState = (newData: any) =>
    setPersonalState((prev: any) => ({ ...prev, ...newData }));

  const setStateData = (newData: any) =>
    setState((prev: any) => ({ ...prev, ...newData }));

  useEffect(() => {
    void init().catch((err) => console.error("[init]", err));
  }, [
    personalState?.developerId,
    personalState?.projectId,
    personalState?.project_name,
    personalState?.countryId,
    personalState?.country_name,
    personalState?.stateId,
    personalState?.state_name,
    personalState?.city,
    personalState?.city_name,
    personalState?.zone,
    personalState?.zoneId,
  ]);

  useEffect(() => {
    if (state?.openTeamModal) {
      void handleAssignTeam();
    }
  }, [state?.openTeamModal])

  useEffect(() => {
    const teamIds = (personalState?.AssignTo || []).map((t: any) => t.id).filter(Boolean);
    if (teamIds.length === 0) {
      setStateData({ leadOptionMembers: [] });
      return;
    }
    const fetchMembers = async () => {
      try {
        const membersRes = await getAllTeamMembersDataProject({
          team_id: teamIds,
          projectId: personalState?.projectId || "",
        });
        const membersData = membersRes?.data || [];
        const members = membersData
          .flatMap((member: any) =>
            (member?.team_members || []).map((mem: any) => ({
              pId: member._id,
              id: mem.users?.[0]?._id,
              label: mem.users?.[0]?.name,
            }))
          )
          .filter((mem: any) => mem.id && mem.label);
        setStateData({ leadOptionMembers: members });
      } catch (err) {
        console.error("Failed to fetch team members", err);
      }
    };
    void fetchMembers();
  }, [JSON.stringify((personalState?.AssignTo || []).map((t: any) => t.id))])

  const handleAssignTeam = async () => {
    try {
      const teamData = await getAllTeamDataProject("");
      if (teamData?.data) setStateData({ teamData });
    } catch (error: any) {
      console.error(error);
    }
  };

  useEffect(() => {
    void projectDetails().catch((err) => console.error("[projectDetails]", err));
  }, [props?.idd, props?.data]);

  const projectDetails = async () => {
    if (!props?.idd && !props?.data) return;

    const applyProject = async (project: any) => {
      if (!project || typeof project !== "object") return;

      let teamRoles: any[] = [];
      let leadOptionMembers: any[] = [];
      let AssignToUser: any[] = [];

      const assignTeamIds = Array.isArray(project.AssignTo)
        ? project.AssignTo.map((t: any) => toId(t)).filter(Boolean)
        : parseAssignTeamIds(project.AssignTo);
      const assignUserIds = Array.isArray(project.AssignToUser)
        ? project.AssignToUser.map((u: any) => toId(u)).filter(Boolean)
        : parseAssignUserIds(project.AssignToUser);

      // If list API already sent populated objects with names, use them
      if (
        Array.isArray(project.AssignTo) &&
        project.AssignTo.some((t: any) => t?.team_name || t?.value || t?.name)
      ) {
        teamRoles = project.AssignTo.map((t: any) =>
          namedTeamRole(t._id || t.id, {
            _id: t._id || t.id,
            team_name: t.team_name || t.name || (isObjectId(t.value) ? "" : t.value),
          })
        ).filter(Boolean);
      }

      if (
        Array.isArray(project.AssignToUser) &&
        project.AssignToUser.some((u: any) => u?.name || u?.label)
      ) {
        AssignToUser = project.AssignToUser.map((u: any) => ({
          id: toId(u),
          label: u?.name || u?.label || toId(u),
          selected: 1,
        })).filter((u: any) => u.id);
      }

      // Resolve team / member names from IDs (by-id API returns string IDs only)
      try {
        if (assignTeamIds.length > 0 && teamRoles.length === 0) {
          const teamResponse = await getAllTeamDataProject("");
          const teamData = teamResponse?.data || [];
          const allTeamsFlat = teamData.flatMap((team: any) => [
            team,
            ...(team.sub_teams || []),
          ]);
          teamRoles = assignTeamIds
            .map((id: string) => {
              const matched = allTeamsFlat.find(
                (team: any) => String(team._id) === String(id)
              );
              return namedTeamRole(id, matched);
            })
            .filter(Boolean);
        }

        if (assignTeamIds.length > 0) {
          const membersRes = await getAllTeamMembersDataProject({
            team_id: assignTeamIds,
            projectId: project.projectId || "",
          });
          const membersData = membersRes?.data || [];
          leadOptionMembers = membersData
            .flatMap((member: any) =>
              (member?.team_members || []).map((mem: any) => ({
                pId: member._id,
                id: mem.users?.[0]?._id,
                label: mem.users?.[0]?.name,
              }))
            )
            .filter((mem: any) => mem.id);

          const selectedIdSet = new Set(assignUserIds.map(String));
          const isRealName = (label: any, id: any) => {
            const l = String(label || "").trim();
            const i = String(id || "").trim();
            if (!l || l === i) return false;
            // ignore raw object-id style labels
            if (/^[a-f0-9]{24}$/i.test(l)) return false;
            return true;
          };

          // Only keep members that resolve to a real name (drop stale/orphan IDs)
          const namedFromList = leadOptionMembers
            .filter(
              (user) =>
                selectedIdSet.has(String(user.id)) &&
                isRealName(user.label, user.id)
            )
            .map((user) => ({ ...user, selected: 1 }));

          if (namedFromList.length > 0) {
            AssignToUser = namedFromList;
          } else if (AssignToUser.length > 0) {
            AssignToUser = AssignToUser.filter((u) =>
              isRealName(u.label, u.id)
            ).map((u) => ({ ...u, selected: 1 }));
          }

          // Dedupe by id
          AssignToUser = Array.from(
            new Map(AssignToUser.map((u) => [String(u.id), u])).values()
          );
        }
      } catch (err) {
        console.error("Failed to resolve assign team/members", err);
        // Do not seed AssignTo or members with raw IDs — a missing team stays hidden.
      }

      const cityRaw = project.city;
      const cityValue =
        cityRaw != null && cityRaw !== "" ? String(cityRaw) : "";

      setPersonalDetailState({
        _id: project._id,
        developerId: toId(project.developerId),
        developer_name: project.developer_name || "",
        projectId: toId(project.projectId),
        project_name: project.project_name || "",
        countryId: toId(project.countryId),
        country_name: project.country_name || "",
        stateId: toId(project.stateId),
        state_name: project.state_name || "",
        city: cityValue,
        city_name:
          project.city_name ||
          (cityValue && !/^[a-f0-9]{24}$/i.test(cityValue) ? cityValue : ""),
        zone: project.zone || project.zone_name || "",
        zoneId: toId(project.zoneId) || "",
        zipcode:
          project.zipcode ||
          project.zip_code ||
          project.zip ||
          project.postalCode ||
          "",
        lat: project.lat,
        lng: project.lng,
        fullAddress: project.fullAddress || "",
        description: project.description || "",
        AssignTo: teamRoles,
        AssignToUser,
        organizationId: project.organizationId
          ? String(project.organizationId)
          : "",
      });

      setStateData({
        leadOptionMembers:
          leadOptionMembers.length > 0 ? leadOptionMembers : AssignToUser,
      });
    };

    // Prefill quickly from list-row cache, then overwrite with by-id API
    if (props?.data) {
      await applyProject(props.data);
    }

    if (!props?.idd) return;

    try {
      const response = await getProjectDetails(props.idd);
      if (response?.data) {
        await applyProject(response.data);
      }
    } catch (error: any) {
      if (!props?.data) {
        openErrorNotification(
          typeof error?.message === "string"
            ? error.message
            : notifiers.GENERIC_ERROR
        );
      }
    }
  };


  const init = async () => {
    try {
      const developerRes = await getDeveloper(personalState?.countryId);
      const developerList = (developerRes?.data || []).map((i: any) => ({
        id: i._id,
        label: i.developer_name,
      }));

      let projectList: any[] = [];
      if (personalState.developerId) {
        const projectRes = await getProjectByDeveloper(personalState.developerId);
        projectList = (projectRes?.data || []).map((i: any) => ({
          id: i._id,
          label: i.project_name,
        }));
      }

      const locationRes = await getLocationMeta();
      const locationData = locationRes?.data || [];

      const countryList = locationData.map((country: any) => ({
        id: String(country._id),
        label: country.country_name,
      }));

      const selectedCountry =
        locationData.find(
          (country: any) =>
            String(country._id) === String(personalState?.countryId)
        ) ||
        locationData.find(
          (country: any) =>
            country.country_name === personalState?.country_name ||
            country.country_name === personalState?.countryId
        );

      const stateList = (selectedCountry?.states || []).map((s: any) => ({
        id: String(s._id),
        label: s.name,
      }));

      const selectedState =
        (selectedCountry?.states || []).find(
          (s: any) => String(s._id) === String(personalState?.stateId)
        ) ||
        (selectedCountry?.states || []).find(
          (s: any) =>
            s.name === personalState?.state_name ||
            s.name === personalState?.stateId
        );

      const cityList = (selectedState?.cities || []).map((city: any) => ({
        id: String(city._id),
        label: city.name,
      }));

      const selectedCity =
        (selectedState?.cities || []).find(
          (c: any) =>
            String(c._id) === String(personalState?.city) ||
            c.name === personalState?.city
        ) ||
        (selectedState?.cities || []).find(
          (c: any) => c.name === personalState?.city_name
        );

      const nestedZoneList = (selectedCity?.zones || []).map((zone: any) => ({
        id: String(zone._id),
        label: zone.name,
      }));

      let zoneList = nestedZoneList;
      if (selectedCity?._id) {
        try {
          const zoneRes = await getZone({
            cityId: String(selectedCity._id),
            limit: 500,
            page: 1,
          });
          const masterZones = (zoneRes?.data || []).map((z: any) => ({
            id: String(z._id),
            label: z.zone_name || z.name,
          }));
          if (masterZones.length) {
            zoneList = masterZones;
          }
        } catch (e) {
          console.warn("Zone master fetch failed, using nested zones", e);
        }
      }

      // Keep name labels visible even if id resolution is pending
      const withFallback = (
        list: { id: string; label: string }[],
        idVal: any,
        nameVal: any
      ) => {
        const id = idVal != null && idVal !== "" ? String(idVal) : "";
        const name = nameVal != null && nameVal !== "" ? String(nameVal).trim() : "";
        if (id && !list.some((o) => String(o.id) === id)) {
          // If name matches an existing option, don't duplicate — caller will sync id
          const byName = name
            ? list.find(
                (o) => o.label.trim().toLowerCase() === name.toLowerCase()
              )
            : null;
          if (byName) return list;
          return [{ id, label: name || id }, ...list];
        }
        if (
          !id &&
          name &&
          !list.some(
            (o) => o.label.trim().toLowerCase() === name.toLowerCase()
          )
        ) {
          return [{ id: name, label: name }, ...list];
        }
        return list;
      };

      const matchByIdOrName = (
        list: { id: string; label: string }[],
        idVal: any,
        nameVal: any
      ) => {
        const id = idVal != null && idVal !== "" ? String(idVal) : "";
        const name = nameVal != null && nameVal !== "" ? String(nameVal).trim() : "";
        return (
          list.find((o) => id && String(o.id) === id) ||
          list.find(
            (o) =>
              name &&
              o.label.trim().toLowerCase() === name.toLowerCase()
          ) ||
          null
        );
      };

      const resolvedProjectList = withFallback(
        projectList,
        personalState?.projectId,
        personalState?.project_name
      );
      const resolvedDeveloperList = withFallback(
        developerList,
        personalState?.developerId,
        personalState?.developer_name
      );
      const resolvedZoneList = withFallback(
        zoneList,
        personalState?.zoneId,
        personalState?.zone
      );

      setStateData({
        developerList: resolvedDeveloperList,
        projectList: resolvedProjectList,
        countryList: withFallback(
          countryList,
          personalState?.countryId,
          personalState?.country_name
        ),
        stateList: withFallback(
          stateList,
          personalState?.stateId,
          personalState?.state_name
        ),
        cityList: withFallback(
          cityList,
          personalState?.city,
          personalState?.city_name
        ),
        zoneList: resolvedZoneList,
      });

      // Sync resolved location IDs so YhSelect values match options
      const locationPatch: any = {};
      if (selectedCountry) {
        const cid = String(selectedCountry._id);
        if (String(personalState?.countryId || "") !== cid) {
          locationPatch.countryId = cid;
        }
        if (!personalState?.country_name) {
          locationPatch.country_name = selectedCountry.country_name;
        }
      }
      if (selectedState) {
        const sid = String(selectedState._id);
        if (String(personalState?.stateId || "") !== sid) {
          locationPatch.stateId = sid;
        }
        if (!personalState?.state_name) {
          locationPatch.state_name = selectedState.name;
        }
      }
      if (selectedCity) {
        const cityId = String(selectedCity._id);
        if (String(personalState?.city || "") !== cityId) {
          locationPatch.city = cityId;
        }
        if (!personalState?.city_name) {
          locationPatch.city_name = selectedCity.name;
        }
      }

      // Zone is often saved as name only — map to option id
      const matchedZone = matchByIdOrName(
        resolvedZoneList,
        personalState?.zoneId,
        personalState?.zone
      );
      if (matchedZone) {
        if (String(personalState?.zoneId || "") !== String(matchedZone.id)) {
          locationPatch.zoneId = matchedZone.id;
        }
        if (
          String(personalState?.zone || "").trim() !==
          String(matchedZone.label).trim()
        ) {
          locationPatch.zone = matchedZone.label;
        }
      }

      // Project name fallback when only name / id is present
      const matchedProject = matchByIdOrName(
        resolvedProjectList,
        personalState?.projectId,
        personalState?.project_name
      );
      if (matchedProject) {
        if (String(personalState?.projectId || "") !== String(matchedProject.id)) {
          locationPatch.projectId = matchedProject.id;
        }
        if (!personalState?.project_name) {
          locationPatch.project_name = matchedProject.label;
        }
      } else if (
        !personalState?.projectId &&
        personalState?.project_name
      ) {
        // Keep a selectable value even if master list is empty
        locationPatch.projectId = personalState.project_name;
      }

      const matchedDeveloper = matchByIdOrName(
        resolvedDeveloperList,
        personalState?.developerId,
        personalState?.developer_name
      );
      if (matchedDeveloper) {
        if (
          String(personalState?.developerId || "") !==
          String(matchedDeveloper.id)
        ) {
          locationPatch.developerId = matchedDeveloper.id;
        }
        if (!personalState?.developer_name) {
          locationPatch.developer_name = matchedDeveloper.label;
        }
      }

      if (Object.keys(locationPatch).length > 0) {
        setPersonalDetailState(locationPatch);
      }
    } catch (err: any) {
      console.error("Error fetching initial data:", err);
      openErrorNotification(
        typeof err?.message === "string" ? err.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleParentTeam = (team: any) => {

    const currentAssignTo = personalState?.AssignTo || [];

    const isAlreadySelected = currentAssignTo.some(
      (selected: { id: any }) => String(selected.id) === String(team._id)
    );
    let updatedSelectedTeams;
    if (isAlreadySelected) {
      updatedSelectedTeams = (currentAssignTo || [])?.filter((selected: { id: any }) => selected.id !== team._id);
    } else {
      updatedSelectedTeams = [
        ...currentAssignTo,
        {
          id: team._id,
          value: team.team_name,
        },
      ];
    }

    setPersonalDetailState({
      AssignTo: updatedSelectedTeams,
    });
  };

  const renderTeams = (teams: any) => {
    const list = Array.isArray(teams) ? teams : teams?.data || [];
    return list.map((team: any) => {
      const isChecked = personalState?.AssignTo?.some(
        (selected: { id: any }) => String(selected.id) === String(team._id)
      );

      return (
        <Box key={team._id}>
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            pl={1}
          >
            <Typography variant="body1" mr={1} style={{ fontSize: "12px" }}>
              {team.team_name}
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={isChecked} />}
              label=""
              value={team._id}
              style={{ marginLeft: "auto" }}
              onClick={() => handleParentTeam(team)}
            />
          </Box>
        </Box>
      );
    });
  };

  const submitForm = async () => {
    const validationErrors = validatePersonalDetail(personalState);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return; // stop submission if invalid
    }
    try {
      let idMembers = [];
      let teamMembers = [];
      if (personalState.AssignTo) {
        idMembers = (personalState?.AssignToUser || []).map(
          (item: any) => `${item.id}`
        );
        teamMembers = personalState.AssignTo.filter(
          (item: any) =>
            isObjectId(item?.id) && item?.value && !isObjectId(item.value)
        ).map((item: any) => `'${item.id}'`);
      }
      const zoneId = String(personalState.zoneId || "").trim();
      const body = {
        _id: personalState?._id,
        developerId: isObjectId(personalState.developerId)
          ? personalState.developerId
          : undefined,
        projectId: personalState.projectId,
        countryId: personalState.countryId,
        stateId: personalState.stateId,
        city: personalState.city,
        zone: personalState.zone,
        ...(isObjectId(zoneId) ? { zoneId } : {}),
        zipcode: personalState.zipcode,
        fullAddress: personalState.fullAddress,
        description: personalState.description || "",
        lat: personalState.lat,
        lng: personalState.lng,
        AssignTo: teamMembers.length ? teamMembers.join(",") : "NA",
        AssignToUser: idMembers.length ? idMembers : [],
        organizationId: isObjectId(personalState.organizationId)
          ? personalState.organizationId
          : undefined,
      };
      let response = []
      if (personalState?._id) {
        response = await updateProjectDetail(body)

      }
      else {

        response = await addProjectDetail(body)
        props.setIdd(response.data._id);
      }
      if (response?.success) {
        openSuccessNotification(response?.message);
        props.nextStep?.();
      } else {
        openErrorNotification(
          response?.message || "Could not save project details"
        );
      }
    }
    catch (error: any) {
      openErrorNotification(
        typeof error?.message === "string"
          ? error.message
          : "Could not save project details"
      );
    }
  }
  return (
    <div className="w-full">
      {/* —— Project Location —— */}
      <div className="mb-5">
        <h2 className="text-xl font-bold tracking-tight text-[#111827]">
          Project Location
        </h2>
        <p className="mt-1 text-sm text-[#6B7280]">
          Provide essential information about the project.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <OrganizationSelect
            label="Organization"
            required
            value={personalState?.organizationId || ""}
            onChange={(organizationId) =>
              setPersonalDetailState({ organizationId })
            }
          />
        </div>
        <YhSelect
          label="Country"
          placeholder="Select Country"
          searchPlaceholder="Search country..."
          showOptionIcon={false}
          requiredMark
          error={errors.countryId}
          value={personalState?.countryId || personalState?.country_name || ""}
          options={(state?.countryList || []).map((o: any) => ({
            label: o.label,
            value: String(o.id),
          }))}
          onChange={(e) => {
            const id = String(e.target.value || "");
            const opt = (state?.countryList || []).find(
              (o: any) => String(o.id) === id
            );
            setPersonalDetailState({
              countryId: opt?.id || "",
              country_name: opt?.label || "",
              stateId: "",
              state_name: "",
              city_name: "",
              city: "",
              zone: "",
              zoneId: "",
              fullAddress: "",
              zipcode: "",
            });
            if (errors.countryId) {
              setErrors((prev) => ({ ...prev, countryId: "" }));
            }
          }}
        />

        <YhSelect
          label="State"
          placeholder="Select State"
          searchPlaceholder="Search state..."
          showOptionIcon={false}
          requiredMark
          error={errors.stateId}
          value={personalState?.stateId || personalState?.state_name || ""}
          options={(state?.stateList || []).map((o: any) => ({
            label: o.label,
            value: String(o.id),
          }))}
          onChange={(e) => {
            const id = String(e.target.value || "");
            const opt = (state?.stateList || []).find(
              (o: any) => String(o.id) === id
            );
            setPersonalDetailState({
              stateId: opt?.id || "",
              state_name: opt?.label || "",
              city_name: "",
              city: "",
              zone: "",
              zoneId: "",
              fullAddress: "",
            });
            if (errors.stateId) {
              setErrors((prev) => ({ ...prev, stateId: "" }));
            }
          }}
        />

        <YhSelect
          label="City"
          placeholder="Select City"
          searchPlaceholder="Search city..."
          showOptionIcon={false}
          requiredMark
          error={errors.city}
          value={personalState?.city || personalState?.city_name || ""}
          options={(state?.cityList || []).map((o: any) => ({
            label: o.label,
            value: String(o.id),
          }))}
          onChange={async (e) => {
            const id = String(e.target.value || "");
            const opt = (state?.cityList || []).find(
              (o: any) => String(o.id) === id
            );
            if (!opt) return;
            setPersonalDetailState({ loading: true });
            const fullAddressQuery = `${opt.label}, ${personalState?.state_name}, ${personalState?.country_name}`;
            try {
              const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
                  fullAddressQuery
                )}`
              );
              const data = await res.json();
              if (data?.length > 0) {
                const location = data[0];
                setPersonalDetailState({
                  city_name: opt.label,
                  city: opt.id,
                  lat: parseFloat(location.lat),
                  lng: parseFloat(location.lon),
                  zoneId: "",
                  zone: "",
                  fullAddress: "",
                });
              } else {
                setPersonalDetailState({
                  city: opt.id,
                  city_name: opt.label,
                  zoneId: "",
                  zone: "",
                  fullAddress: "",
                });
              }
              if (errors.city) {
                setErrors((prev) => ({ ...prev, city: "" }));
              }
            } catch (error) {
              console.error("Geocode fetch failed", error);
              setPersonalDetailState({
                city: opt.id,
                city_name: opt.label,
              });
            } finally {
              setPersonalDetailState({ loading: false });
            }
          }}
        />

        <YhSelect
          label="Zone"
          placeholder="Select Zone"
          searchPlaceholder="Search zone..."
          showOptionIcon={false}
          requiredMark
          error={errors.zoneId || errors.zone}
          value={
            personalState?.zoneId ||
            (state?.zoneList || []).find(
              (o: any) =>
                String(o.label).trim().toLowerCase() ===
                String(personalState?.zone || "")
                  .trim()
                  .toLowerCase()
            )?.id ||
            personalState?.zone ||
            ""
          }
          options={(state?.zoneList || []).map((o: any) => ({
            label: o.label,
            value: String(o.id),
          }))}
          onChange={(e) => {
            const id = String(e.target.value || "");
            const opt = (state?.zoneList || []).find(
              (o: any) => String(o.id) === id
            );
            setPersonalDetailState({
              zoneId: opt?.id || "",
              zone: opt?.label || "",
            });
            if (errors.zoneId || errors.zone) {
              setErrors((prev) => ({ ...prev, zoneId: "", zone: "" }));
            }
          }}
        />

        <YhInput
          label="Zip Code"
          placeholder="Enter Zip Code"
          value={personalState?.zipcode ?? ""}
          onChange={(e) =>
            setPersonalDetailState({ zipcode: e.target.value })
          }
        />

        <YhInput
          label="Sector"
          placeholder="Enter Sector"
          value={personalState?.fullAddress ?? ""}
          onChange={(e) =>
            setPersonalDetailState({ fullAddress: e.target.value })
          }
        />
      </div>

      {personalState?.city ? (
        <div className="relative mt-5 overflow-hidden rounded-2xl border border-[#EEF0F5]">
          {personalState?.loading ? (
            <div className="pointer-events-none absolute inset-0 z-[1100] flex items-center justify-center bg-white/70 text-sm text-[#6B7280]">
              Loading map…
            </div>
          ) : null}
          <MapPicker
            setPersonalDetailState={setPersonalDetailState}
            lat={personalState?.lat}
            lng={personalState?.lng}
            isEditing={!!personalState?.zipcode}
          />
        </div>
      ) : null}

      {/* —— Project Detail —— */}
      <div className="mb-4 mt-8 border-t border-[#EEF0F5] pt-6">
        <h2 className="text-xl font-bold tracking-tight text-[#111827]">
          Project Detail
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <YhSelect
          label="Developer Name"
          placeholder="Search or select developer name"
          searchPlaceholder="Search developer..."
          showOptionIcon={false}
          value={personalState?.developerId || ""}
          options={(state?.developerList || []).map((o: any) => ({
            label: o.label,
            value: String(o.id),
          }))}
          onChange={(e) => {
            const id = String(e.target.value || "");
            const opt = (state?.developerList || []).find(
              (o: any) => String(o.id) === id
            );
            setPersonalDetailState({
              developerId: opt?.id || "",
              developer_name: opt?.label || "",
              projectId: "",
              project_name: "",
            });
          }}
        />

        <YhSelect
          label="Project Name"
          placeholder="Search or select project name"
          searchPlaceholder="Search project..."
          showOptionIcon={false}
          requiredMark
          error={errors.projectId}
          value={
            personalState?.projectId ||
            (state?.projectList || []).find(
              (o: any) =>
                String(o.label).trim().toLowerCase() ===
                String(personalState?.project_name || "")
                  .trim()
                  .toLowerCase()
            )?.id ||
            personalState?.project_name ||
            ""
          }
          options={(state?.projectList || []).map((o: any) => ({
            label: o.label,
            value: String(o.id),
          }))}
          onChange={(e) => {
            const id = String(e.target.value || "");
            const opt = (state?.projectList || []).find(
              (o: any) => String(o.id) === id
            );
            setPersonalDetailState({
              projectId: opt?.id || "",
              project_name: opt?.label || "",
            });
            if (errors.projectId) {
              setErrors((prev) => ({ ...prev, projectId: "" }));
            }
          }}
        />
      </div>

      {/* —— Project Allotment —— */}
      <div className="mb-4 mt-8 border-t border-[#EEF0F5] pt-6">
        <h2 className="text-xl font-bold tracking-tight text-[#111827]">
          Project Allotment
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <YhInput
          label="Assigned To"
          placeholder="Select"
          readOnly
          value={
            personalState?.AssignTo?.length > 0
              ? personalState.AssignTo.map((team: any) => team?.value)
                  .filter(
                    (name: any) => name && !isObjectId(name)
                  )
                  .join(", ")
              : ""
          }
          onClick={() => setStateData({ openTeamModal: true })}
        />

        {personalState?.AssignTo?.length ? (
          <YhMultiSelect
            label="Assigned To Member"
            placeholder="Select options"
            searchPlaceholder="Search member..."
            showOptionIcon={false}
            maxVisibleChips={10}
            options={(
              state?.leadOptionMembers ||
              uniqueLeadOptionMembers ||
              []
            ).map((o: any) => ({
              label: o.label,
              value: String(o.id),
            }))}
            value={(uniqueSelectedOptionMembers || []).map((o: any) =>
              String(o.id)
            )}
            onChange={(values) => {
              const source =
                state?.leadOptionMembers || uniqueLeadOptionMembers || [];
              const updated = source
                .filter((o: any) =>
                  values.map(String).includes(String(o.id))
                )
                .map((item: any) => ({ ...item, selected: 1 }));
              setStateData({ AssignToUser: updated });
              setPersonalDetailState({ AssignToUser: updated });
            }}
          />
        ) : null}

        <YhInput
          label="Project Description"
          placeholder="Enter project description"
          multiline
          rows={4}
          value={personalState?.description ?? ""}
          onChange={(e) =>
            setPersonalDetailState({ description: e.target.value })
          }
        />
      </div>

      <div className="mt-6 flex justify-end">
        <YhButton variant="primary" onClick={submitForm}>
          Continue
        </YhButton>
      </div>

      {state?.apiResponse && <CustomLoader apiResponse={false} />}
      <Dialog
        open={state?.openTeamModal}
        onClose={() => setStateData({ openTeamModal: false })}
        aria-describedby="alert-dialog-slide-description"
        maxWidth="sm"
        fullWidth
        sx={MetaListStyles.radioGroup}
        style={{
          maxWidth: "700px",
          margin: "0 auto",
        }}
      >
        <Typography sx={CustomPageHeaderStyles.heading} p={2}>
          Select Team
        </Typography>
        <Divider />
        <Box
          style={{
            maxHeight: "500px",
            overflowY: "auto",
            padding: "8px",
          }}
        >
          <Box sx={MetaListStyles.radioGroup}>{renderTeams(state?.teamData)}</Box>
        </Box>
      </Dialog>
    </div>
  );
};

export default PersonalDetails;
